/**
 * Scenario Comparison Panel
 * Side-by-side comparison of saved future closure scenarios
 */

import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../utils/financialFormat';
import {
  getSavedScenarios,
  saveScenario,
  deleteScenario,
  SavedScenario,
} from '../services/futureClosureApi';

interface ScenarioComparisonPanelProps {
  reportId: string;
  currentCalculationId?: string;
  currentResult?: any;
}

const formatPct = (val: number | undefined | null): string => {
  if (val == null) return 'N/A';
  return `${val}%`;
};

export default function ScenarioComparisonPanel({
  reportId,
  currentCalculationId,
  currentResult,
}: ScenarioComparisonPanelProps) {
  const [scenarios, setScenarios] = useState<SavedScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveNotes, setSaveNotes] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const loadScenarios = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSavedScenarios(reportId);
      setScenarios(data.scenarios || []);
    } catch (err: any) {
      console.error('Failed to load scenarios:', err);
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    loadScenarios();
  }, [loadScenarios]);

  const handleSave = async () => {
    if (!currentCalculationId || !saveName.trim()) return;
    try {
      setSaving(true);
      setError(null);
      await saveScenario(reportId, saveName.trim(), currentCalculationId, saveNotes);
      setSuccessMsg(`Scenario "${saveName}" saved`);
      setSaveName('');
      setSaveNotes('');
      setShowSaveForm(false);
      await loadScenarios();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save scenario');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scenarioId: string) => {
    try {
      await deleteScenario(reportId, scenarioId);
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(scenarioId);
        return next;
      });
      await loadScenarios();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete scenario');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedScenarios = scenarios.filter(s => selectedIds.has(s.id));

  // Quick presets for saving
  const presetNames = ['Conservative', 'Base Case', 'Aggressive', 'Settlement Target'];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
          <svg className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
          Scenario Comparison
        </h3>
        {currentCalculationId && (
          <button
            onClick={() => setShowSaveForm(!showSaveForm)}
            className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {showSaveForm ? 'Cancel' : 'Save Current as Scenario'}
          </button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="text-sm rounded-lg px-4 py-2" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
          {error}
        </div>
      )}
      {successMsg && (
        <div className="text-sm rounded-lg px-4 py-2" style={{ backgroundColor: 'transparent', border: '1px solid var(--color-verified)', color: 'var(--color-verified)' }}>
          {successMsg}
        </div>
      )}

      {/* Save Form */}
      {showSaveForm && (
        <div className="rounded-lg p-4 space-y-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Scenario Name</label>
            <div className="flex gap-2 flex-wrap mb-2">
              {presetNames.map(name => (
                <button
                  key={name}
                  onClick={() => setSaveName(name)}
                  className="px-2 py-1 text-xs rounded border transition-colors"
                  style={saveName === name
                    ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', borderColor: 'var(--color-brand)' }
                    : { backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }}
                >
                  {name}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              placeholder="Enter scenario name..."
              className="w-full rounded-lg px-3 py-2 text-sm focus:ring-2"
              style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Notes (optional)</label>
            <input
              type="text"
              value={saveNotes}
              onChange={e => setSaveNotes(e.target.value)}
              placeholder="e.g. For settlement opening position..."
              className="w-full rounded-lg px-3 py-2 text-sm focus:ring-2"
              style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !saveName.trim()}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={saving || !saveName.trim()
              ? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-ghost)' }
              : { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {saving ? 'Saving...' : 'Save Scenario'}
          </button>
        </div>
      )}

      {/* Scenarios List */}
      {loading ? (
        <div className="text-center py-6 text-sm" style={{ color: 'var(--color-text-muted)' }}>Loading scenarios...</div>
      ) : scenarios.length === 0 ? (
        <div className="text-center py-8 rounded-lg border border-dashed" style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border)' }}>
          <svg className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No saved scenarios yet</p>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>Run a calculation and save it as a scenario to begin comparing</p>
        </div>
      ) : (
        <>
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Select 2+ scenarios to compare side-by-side. {selectedIds.size > 0 && `(${selectedIds.size} selected)`}
          </p>
          <div className="space-y-2">
            {scenarios.map(s => (
              <div
                key={s.id}
                className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all"
                style={selectedIds.has(s.id)
                  ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)', boxShadow: '0 0 0 1px var(--color-brand)' }
                  : { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}
                onClick={() => toggleSelect(s.id)}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(s.id)}
                  onChange={() => toggleSelect(s.id)}
                  className="h-4 w-4 rounded"
                  style={{ accentColor: 'var(--color-brand)' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>{s.name}</span>
                    <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>
                      {new Date(s.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                    <span>Demand: <span className="font-semibold" style={{ color: 'var(--color-text-secondary)' }}>{formatCurrency(s.results.total_demand)}</span></span>
                    <span>Aggression: {s.parameters.aggression ?? 'N/A'}</span>
                    <span>Base: {s.parameters.use_best_base ? 'Best' : 'Last'} {s.parameters.base_months}mo</span>
                  </div>
                  {s.notes && <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--color-text-dim)' }}>{s.notes}</p>}
                </div>
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(s.id); }}
                  className="p-1 transition-colors"
                  style={{ color: 'var(--color-text-dim)' }}
                  title="Delete scenario"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Side-by-Side Comparison Table */}
      {selectedScenarios.length >= 2 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <svg className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            Side-by-Side Comparison
          </h4>
          <div className="overflow-x-auto border rounded-lg" style={{ borderColor: 'var(--color-border)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border)' }}>
                  <th className="text-left px-4 py-3 font-semibold w-48" style={{ color: 'var(--color-text-secondary)' }}>Metric</th>
                  {selectedScenarios.map(s => (
                    <th key={s.id} className="text-right px-4 py-3 font-semibold min-w-[140px]" style={{ color: 'var(--color-text-primary)' }}>
                      {s.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--color-border-faint)]">
                {/* Parameters Section */}
                <tr style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                  <td colSpan={selectedScenarios.length + 1} className="px-4 py-2 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-needs-human-text)' }}>
                    Parameters
                  </td>
                </tr>
                <ComparisonRow label="Aggression" scenarios={selectedScenarios} getValue={s => s.parameters.aggression?.toString() ?? 'N/A'} />
                <ComparisonRow label="Base Period" scenarios={selectedScenarios} getValue={s => `${s.parameters.use_best_base ? 'Best' : 'Last'} ${s.parameters.base_months ?? 36}mo`} />
                <ComparisonRow label="Closure Duration" scenarios={selectedScenarios} getValue={s => `${s.parameters.closure_duration_months ?? 36} months`} />

                {/* Results Section */}
                <tr style={{ backgroundColor: 'transparent' }}>
                  <td colSpan={selectedScenarios.length + 1} className="px-4 py-2 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-verified)' }}>
                    Results
                  </td>
                </tr>
                <ComparisonRow
                  label="Total Demand"
                  scenarios={selectedScenarios}
                  getValue={s => formatCurrency(s.results.total_demand)}
                  highlight
                />
                <ComparisonRow label="Lost Profits" scenarios={selectedScenarios} getValue={s => formatCurrency(s.results.lost_profits)} />
                <ComparisonRow label="Shutdown Costs" scenarios={selectedScenarios} getValue={s => formatCurrency(s.results.shutdown_costs)} />
                <ComparisonRow label="Restart Costs" scenarios={selectedScenarios} getValue={s => formatCurrency(s.results.restart_costs)} />

                {/* Continuation Rates */}
                {selectedScenarios[0]?.results?.continuation_percentages && (
                  <>
                    <tr style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                      <td colSpan={selectedScenarios.length + 1} className="px-4 py-2 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-needs-human-text)' }}>
                        Expense Continuation Rates
                      </td>
                    </tr>
                    {Object.keys(selectedScenarios[0].results.continuation_percentages).map(key => (
                      <ComparisonRow
                        key={key}
                        label={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        scenarios={selectedScenarios}
                        getValue={s => formatPct(s.results.continuation_percentages?.[key])}
                      />
                    ))}
                  </>
                )}

                {/* Delta Row */}
                <tr className="border-t-2" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                  <td className="px-4 py-3 font-bold text-xs uppercase" style={{ color: 'var(--color-needs-human-text)' }}>
                    Demand Spread
                  </td>
                  <td colSpan={selectedScenarios.length} className="px-4 py-3 text-right font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                    {(() => {
                      const demands = selectedScenarios.map(s => s.results.total_demand);
                      const min = Math.min(...demands);
                      const max = Math.max(...demands);
                      return `${formatCurrency(min)} - ${formatCurrency(max)} (${formatCurrency(max - min)} spread)`;
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ComparisonRow({
  label,
  scenarios,
  getValue,
  highlight = false,
}: {
  label: string;
  scenarios: SavedScenario[];
  getValue: (s: SavedScenario) => string;
  highlight?: boolean;
}) {
  const values = scenarios.map(s => getValue(s));
  // Find max for highlighting
  const numericValues = scenarios.map(s => {
    const v = getValue(s).replace(/[^0-9.-]/g, '');
    return parseFloat(v) || 0;
  });
  const maxIdx = numericValues.indexOf(Math.max(...numericValues));

  return (
    <tr style={highlight ? { backgroundColor: 'transparent' } : undefined}>
      <td className={`px-4 py-2 ${highlight ? 'font-semibold' : ''}`} style={{ color: 'var(--color-text-secondary)' }}>{label}</td>
      {values.map((val, i) => (
        <td
          key={i}
          className={`px-4 py-2 text-right tabular-nums ${
            highlight
              ? 'font-bold'
              : i === maxIdx && numericValues[maxIdx] > 0
              ? 'font-semibold'
              : ''
          }`}
          style={{
            color: highlight
              ? 'var(--color-verified)'
              : i === maxIdx && numericValues[maxIdx] > 0
              ? 'var(--color-text-primary)'
              : 'var(--color-text-secondary)'
          }}
        >
          {val}
        </td>
      ))}
    </tr>
  );
}
